// Shell mobile Life — remplace la NavBar desktop en contexte mobile
// (WebView Capacitor ou fenêtre ≤ 768 px). Design V3 validé :
// DEV/mockup_mobile_navigation_v3.html (thème sombre DA SmartFeed).
//
// Il reprend les responsabilités « vitales » de la NavBar :
//   - fetch de l'utilisateur (/api/user/user/) → redux + setUserSync,
//     réconciliation de la langue (LAP-733 / LAP-752),
//   - notifications (fetch initial + WebSocket) → pastille sur la cloche,
// et y ajoute le shell V3 : header, tab bar flottante + FAB, feuille
// d'ajout rapide, pont natif (retour Android + deep links).
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import WebSocket from 'react-websocket'
import { setSiteUser } from 'reduxLib/slices/userSlice'
import { clearClientSession } from 'utils/session'
import { makeGetRequest, makePatchRequest, getWsUrl } from 'utils/api'
import {
  initPushNotifications,
  teardownPushNotifications,
} from 'utils/push'
import i18n, { SUPPORTED_LANGUAGES } from '../../i18n'
import MobileHeader from './MobileHeader'
import MobileTabBar from './MobileTabBar'
import QuickAddSheet from './QuickAddSheet'
import NativeBridge from './NativeBridge'
import './mobile.css'

const MobileShell = ({ setUserSync }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const token = localStorage.getItem('token')

  const [user, setUser] = useState({})
  const [hasUnread, setHasUnread] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const mounted = useRef(true)

  const wsUrl = `${getWsUrl()}/ws/notifications/?app=${token}`

  // Classe body : padding bas global (tab bar flottante) + fond sombre.
  useEffect(() => {
    document.body.classList.add('life-mobile-shell')
    return () => document.body.classList.remove('life-mobile-shell')
  }, [])

  useEffect(() => {
    mounted.current = true
    fetchUser()
    // Push natives (FCM) : permission + enregistrement du token, et deep
    // link au tap sur une notification. No-op hors app native.
    initPushNotifications(navigate)
    return () => {
      mounted.current = false
      teardownPushNotifications()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchUser = async () => {
    try {
      const response = await makeGetRequest('/api/user/user/', {}, true)
      if (response.status === 200) {
        dispatch(setSiteUser(response.data))
        if (mounted.current) setUser(response.data)

        // LAP-733 (GE022) + LAP-752 — même réconciliation de langue que la
        // NavBar desktop : le choix explicite fait sur les pages d'auth
        // prime sur la valeur backend, sinon on suit le backend.
        try {
          let explicitChoice = null
          try {
            explicitChoice = window.localStorage.getItem('i18nExplicitChoice')
          } catch (_) {
            explicitChoice = null
          }
          const backendLang = response.data?.language
          const validExplicit =
            explicitChoice && SUPPORTED_LANGUAGES.includes(explicitChoice)

          if (validExplicit && explicitChoice !== backendLang) {
            if (i18n.language !== explicitChoice) {
              i18n.changeLanguage(explicitChoice)
            }
            try {
              await makePatchRequest(
                '/api/user/preferences/',
                { language: explicitChoice },
                {},
                true,
              )
            } catch (_) {
              // Best-effort : l'UI a déjà basculé localement.
            }
            try {
              window.localStorage.removeItem('i18nExplicitChoice')
            } catch (_) {}
          } else if (
            backendLang &&
            SUPPORTED_LANGUAGES.includes(backendLang) &&
            i18n.language !== backendLang
          ) {
            i18n.changeLanguage(backendLang)
          }
        } catch (_) {
          // Jamais bloquer l'auth sur un souci i18n.
        }

        getNotifications()
        setUserSync(true)
      } else {
        setUserSync(false)
        clearClientSession(dispatch)
        navigate('/login')
      }
    } catch (error) {
      console.error('Error fetching user:', error)
      setUserSync(false)
      clearClientSession(dispatch)
      navigate('/login')
    }
  }

  const getNotifications = async () => {
    try {
      const response = await makeGetRequest('/api/user/get-notifications/', {}, true)
      if (response.status === 200 && mounted.current) {
        setHasUnread(Array.isArray(response.data) && response.data.length > 0)
      }
    } catch (_) {
      // Pastille best-effort.
    }
  }

  const handleWsData = () => {
    // Une notification vient d'arriver : pastille + garde-fou session
    // (même logique que la NavBar : token disparu → retour login).
    if (!localStorage.getItem('token')) {
      clearClientSession(dispatch)
      navigate('/login')
      return
    }
    setHasUnread(true)
  }

  return (
    <>
      <MobileHeader user={user} hasUnread={hasUnread} />
      <MobileTabBar addOpen={addOpen} onToggleAdd={setAddOpen} />
      <QuickAddSheet open={addOpen} onClose={() => setAddOpen(false)} />
      <NativeBridge addOpen={addOpen} onCloseAdd={() => setAddOpen(false)} />
      <WebSocket url={wsUrl} onMessage={handleWsData} />
    </>
  )
}

export default MobileShell
